// ══════════════════════════════════════════════════════════
//  #FLEET_RIG_MAP_V1 (2026-10-08) — where the rigs are, on Bob's Rig Board.
//
//  Bill: "we could pull MAPs here from locations from RIGS and GPS Units? … a small Map with tiny icons to maybe
//  expand for full screen if/when needed. That would be sexy."
//
//  Vermeer sends a GPS position with every report and api/oem-hours-sync.js already stores it (lat / lon /
//  locationAt on each machine in `fleet-oem-hours`), so the rig side needs nothing new. A small map with one dot
//  per rig sits on the board; "Full screen" opens the same map large, with zoom, unit labels and a click-for-details
//  popup. Read-only.
//
//  The map library (Leaflet 1.9.4) is loaded from unpkg the first time a map is shown — pinned to that exact
//  version and checked against its published fingerprint — so nothing was added to package.json. Street tiles are
//  OpenStreetMap, tinted dark to sit in the SHOP theme. If either cannot be reached the map says so and the 📍 links
//  on the rig cards still work.
//
//  Trucks, VACs and trailers (Geotab) are NOT on this map — they have their own, FleetGpsMap.js (#FLEET_GPS_MAP_V1),
//  which borrows the map loader below. This one stays the iron: Vermeer now, Ditch Witch / Kobelco / Link-Belt as
//  their feeds arrive (anything in `fleet-oem-hours` with a position shows up here with no further work).
// ══════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const LEAFLET_VER = '1.9.4';
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.js`;
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.css`;
const LEAFLET_JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
const LEAFLET_CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
const TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
export const MAP_TILES = TILES;
const NEAR_MILES = 300;   // rigs further than this from the middle of the pack are left off the small map

export const esc = (t) => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

function ensureStyles() {
  if (document.getElementById('dope-rigmap-css')) return;
  const css = document.createElement('link');
  css.id = 'dope-rigmap-css'; css.rel = 'stylesheet'; css.href = LEAFLET_CSS; css.integrity = LEAFLET_CSS_SRI; css.crossOrigin = '';
  document.head.appendChild(css);
  const st = document.createElement('style');
  st.textContent = [
    '.dope-rigmap .leaflet-container{background:#15171A;font:inherit;border-radius:10px}',
    '.dope-rigmap .leaflet-tile-pane{filter:invert(1) hue-rotate(180deg) brightness(.9) contrast(.9) saturate(.6)}',
    '.dope-rigmap .leaflet-popup-content-wrapper,.dope-rigmap .leaflet-popup-tip{background:#22252A;color:#EDEEF0;box-shadow:0 4px 18px #000a}',
    '.dope-rigmap .leaflet-popup-content{margin:10px 12px;font-size:12px;line-height:1.45}',
    '.dope-rigmap .leaflet-popup-content a{color:#FF7A1A;font-weight:800;text-decoration:none}',
    '.dope-rigmap .leaflet-container a.leaflet-popup-close-button{color:#9AA0A8}',
    '.dope-rigmap .leaflet-control-attribution{background:#15171Acc;color:#9AA0A8;font-size:9px}',
    '.dope-rigmap .leaflet-control-attribution a{color:#9AA0A8}',
    '.dope-rigmap .leaflet-bar a{background:#22252A;color:#EDEEF0;border-bottom-color:#3A3D42}',
    '.dope-rigmap .leaflet-bar a:hover{background:#2C3036;color:#fff}',
  ].join('\n');
  document.head.appendChild(st);
}

let leafletPromise = null;
export function loadLeaflet() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.reject(new Error('no page'));
  ensureStyles();
  if (window.L && window.L.map) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = LEAFLET_JS; s.integrity = LEAFLET_JS_SRI; s.crossOrigin = ''; s.async = true;
    s.onload = () => (window.L && window.L.map ? resolve(window.L) : reject(new Error('map library loaded empty')));
    s.onerror = () => { leafletPromise = null; reject(new Error('map library did not load')); };
    document.head.appendChild(s);
  });
  return leafletPromise;
}

const miles = (a, b) => {
  const R = 3958.8, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat), dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};
// The pack = rigs within NEAR_MILES of the middle one. One rig on a job three states away must not shrink the
// small map until the Texas rigs are a single smudge.
export function splitPack(points) {
  if (points.length < 3) return { pack: points, far: [] };
  const med = (arr) => { const s = arr.slice().sort((x, y) => x - y); return s[Math.floor(s.length / 2)]; };
  const mid = { lat: med(points.map(p => p.lat)), lon: med(points.map(p => p.lon)) };
  const pack = points.filter(p => miles(mid, p) <= NEAR_MILES);
  if (pack.length < Math.ceil(points.length / 2)) return { pack: points, far: [] };
  return { pack, far: points.filter(p => miles(mid, p) > NEAR_MILES).map(p => ({ ...p, miles: Math.round(miles(mid, p)) })) };
}

// #RIG_MAP_STALE_V1 (2026-10-15) — Bill: "IRON Map shows 23-2033 still in Michigan but in Jackson MS now?" The factory
// unit only reports while the rig is keyed on, so a rig on a trailer keeps its LAST position until it is started at the
// new job. A position more than a day old now shows as a hollow dashed dot with its age ("23-2033 · 2d") and the card
// says so, instead of reading as where the rig is right now.
const STALE_MS = 24 * 3600 * 1000;
export const staleDays = (p) => { const t = p && p.seenAt ? new Date(p.seenAt).getTime() : NaN; if (!isFinite(t)) return 0; const ms = Date.now() - t; return ms > STALE_MS ? Math.floor(ms / (24 * 3600 * 1000)) : 0; };
const dotHtml = (p, full) => {
  const size = full ? 14 : 11, off = -(size / 2 + 2);
  const sd = staleDays(p);
  const dot = sd
    ? `<span style="display:block;width:${size}px;height:${size}px;border-radius:50%;background:transparent;border:2px dashed ${esc(p.ring || '#9AA0A8')};opacity:.85"></span>`
    : `<span style="display:block;width:${size}px;height:${size}px;border-radius:50%;background:${esc(p.color)};border:2px solid ${esc(p.ring || '#15171A')};box-shadow:0 0 ${p.ring ? 12 : 8}px ${p.ring ? 2 : 0}px ${esc(p.glow || p.color)}"></span>`;
  const label = full ? `<span style="margin-left:4px;font:800 11px/1 -apple-system,Segoe UI,Arial,sans-serif;color:${sd ? '#9AA0A8' : '#EDEEF0'};text-shadow:0 0 3px #000,0 0 3px #000,0 0 6px #000">${esc(p.unit)}${sd ? ` · ${sd}d` : ''}</span>` : '';
  return `<div style="position:absolute;left:${off}px;top:${off}px;display:flex;align-items:center;white-space:nowrap">${dot}${label}</div>`;
};
const popupHtml = (p) => [
  `<div style="font-size:14px;font-weight:900;color:#FF7A1A">${esc(p.unit)}</div>`,
  p.name ? `<div style="color:#9AA0A8;font-size:11px">${esc(p.name)}</div>` : '',
  `<div style="margin-top:4px"><b style="color:${esc(p.color)}">${esc(p.status)}</b>${p.hours ? ` · ${esc(p.hours)} hrs` : ''}</div>`,
  p.fault ? `<div style="color:${esc(p.glow || '#EDEEF0')}">${esc(p.fault)}</div>` : '',
  p.when ? `<div style="color:#9AA0A8;font-size:11px">reported ${esc(p.when)}</div>` : '',
  staleDays(p) ? `<div style="color:#FFB020;font-size:11px;margin-top:3px">⚠ Last position is ${staleDays(p)} day${staleDays(p) === 1 ? '' : 's'} old — the rig has not been keyed on since. If it was hauled, it updates when it is started at the new job.</div>` : '',
  `<div style="margin-top:6px"><a href="https://www.google.com/maps?q=${Number(p.lat).toFixed(5)},${Number(p.lon).toFixed(5)}" target="_blank" rel="noopener noreferrer">Open in Google Maps ↗</a></div>`,
].join('');

function MapView({ points, full, height, focus }) {
  const ref = useRef(null);
  const mapRef = useRef(null);     // #FLEET_GPS_TOPFIND_V1 — so a search pick can fly to a rig
  const markRef = useRef({});
  const [err, setErr] = useState('');
  const sig = JSON.stringify(points.map(p => [p.unit, p.lat, p.lon, p.color, p.ring, p.glow, p.status, p.fault, p.hours]));
  useEffect(() => {
    let map = null, dead = false;
    setErr('');
    loadLeaflet().then((L) => {
      if (dead || !ref.current) return;
      map = L.map(ref.current, { zoomControl: !!full, scrollWheelZoom: !!full, zoomSnap: 0.25, attributionControl: true });
      mapRef.current = map; markRef.current = {};
      L.tileLayer(TILES, { maxZoom: 18, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>' }).addTo(map);
      points.forEach((p) => {
        markRef.current[p.unit] = L.marker([p.lat, p.lon], { title: `${p.unit} — ${p.status}`, icon: L.divIcon({ className: '', html: dotHtml(p, full), iconSize: [0, 0] }) })
          .addTo(map).bindPopup(popupHtml(p), { maxWidth: 240 });
      });
      const shown = full ? points : splitPack(points).pack;
      if (shown.length === 1) map.setView([shown[0].lat, shown[0].lon], 10);
      else map.fitBounds(L.latLngBounds(shown.map(p => [p.lat, p.lon])), { padding: full ? [60, 60] : [22, 22], maxZoom: 12 });
    }).catch((e) => { if (!dead) setErr((e && e.message) || 'map did not load'); });
    return () => { dead = true; mapRef.current = null; markRef.current = {}; if (map) map.remove(); };
  }, [sig, full]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {   // #FLEET_GPS_TOPFIND_V1 — fly to the picked rig and open its card
    if (!focus || !mapRef.current) return;
    const m = markRef.current[focus.unit];
    if (!m) return;
    try { mapRef.current.setView(m.getLatLng(), 14); m.openPopup(); } catch { /* map gone */ }
  }, [focus]);
  return (
    <div className="dope-rigmap" style={{ position: 'relative', height, minHeight: 150 }}>
      <div ref={ref} style={{ position: 'absolute', inset: 0, borderRadius: 10, overflow: 'hidden', background: '#15171A' }} />
      {err ? <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 12, fontSize: 11, color: '#9AA0A8' }}>
        The map could not load ({err}). The 📍 map links on the rig cards still work.</div> : null}
    </div>
  );
}

// points: [{ unit, name, lat, lon, color, ring, glow, status, hours, fault, when }] — ring/glow set = a fault code is open
// #FLEET_MAPS_MINI_V1 (2026-10-09) — `legend`: show the colour key under the small map too (Owner dash / PM board,
// where there are no rig cards beside it to explain the dots). `emptyNote`: text to show instead of nothing when no
// rig has sent a position.
export default function FleetRigMap({ points, theme, tile, checkedAt, legend: showKey, emptyNote }) {
  const [full, setFull] = useState(false);
  const [topFind, setTopFind] = useState('');   // #FLEET_GPS_TOPFIND_V1 — same search box as the GPS map
  const [focus, setFocus] = useState(null);
  const T = theme || {};
  const STEEL = T.steel || '#9AA0A8', TEXT = T.text || '#EDEEF0', ACCENT = T.accent || '#FF7A1A';
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);
  const pts = (points || []).filter(p => p && isFinite(Number(p.lat)) && isFinite(Number(p.lon)));
  if (!pts.length) {
    if (!emptyNote) return null;
    return <div style={{ ...(tile || {}), cursor: 'default', height: '100%', minHeight: 190, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 11, color: STEEL }}>{emptyNote}</div>;
  }
  const { far } = splitPack(pts);
  const btn = { cursor: 'pointer', background: '#15171Aee', color: TEXT, border: `1px solid ${T.panelBorder || '#3A3D42'}`, borderRadius: 6, padding: '3px 9px', fontSize: 10.5, fontWeight: 800 };
  const legend = [['working', T.good || '#4ADE80'], ['idling', T.accent2 || '#FFB020'], ['off / parked', STEEL]];
  const faultN = pts.filter(p => p.ring).length;
  return (
    <div style={{ ...(tile || {}), cursor: 'default', padding: 6, height: '100%', minHeight: showKey ? 190 : 176, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 130 }}>
        <MapView points={pts} full={false} height="100%" />
        <button type="button" onClick={() => setFull(true)} title="Open the map full screen — ✕ or Esc closes it" style={{ ...btn, position: 'absolute', top: 6, right: 6, zIndex: 500 }}>{showKey ? '⤢ Open' : '⤢ Full screen'}</button>
        <div style={{ position: 'absolute', left: 6, top: 6, zIndex: 500, fontSize: 9.5, fontWeight: 900, letterSpacing: .6, textTransform: 'uppercase', color: ACCENT, background: '#15171Acc', borderRadius: 4, padding: '2px 6px',
          maxWidth: 'calc(100% - 100px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {showKey ? `Iron · ${pts.length} rig${pts.length === 1 ? '' : 's'}${checkedAt ? ` · ${checkedAt}` : ''}` : `${pts.length} rig${pts.length === 1 ? '' : 's'} on the map`}</div>
        {far.length ? (
          <button type="button" onClick={() => setFull(true)} title="Open the full map to see it"
            style={{ ...btn, position: 'absolute', left: 6, bottom: showKey ? 22 : 6, zIndex: 500, color: T.accent2 || '#FFB020', fontWeight: 700, maxWidth: 'calc(100% - 12px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {far.map(p => p.unit).join(', ')} {far.length === 1 ? 'is' : 'are'} off this view — {far.map(p => `${p.miles.toLocaleString('en-US')} mi`).join(', ')} away ↗</button>
        ) : null}
      </div>
      {showKey ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 10px', padding: '0 4px 1px', fontSize: 10.5, color: TEXT }}>
          {legend.map(([lbl, c]) => (
            <span key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: c, display: 'inline-block' }} />
              {lbl.charAt(0).toUpperCase() + lbl.slice(1)} <b>{pts.filter(p => p.color === c).length}</b></span>
          ))}
          <span title="A ring around a dot = a fault code is open on that rig (red ring = red code)" style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', border: '2px solid #FFFFFF', boxShadow: `0 0 6px 1px ${T.accent2 || '#FFB020'}`, display: 'inline-block' }} />Fault code <b>{faultN}</b></span>
        </div>
      ) : null}
      {full ? createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#0D0E10', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, "Segoe UI", Roboto, Arial, sans-serif' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '10px 14px', borderBottom: `1px solid ${T.panelBorder || '#3A3D42'}` }}>
            <span style={{ fontSize: 13, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase', color: ACCENT }}>Rig map</span>
            <span style={{ fontSize: 11, color: STEEL }}>{pts.length} rig{pts.length === 1 ? '' : 's'}{checkedAt ? ` · feed checked ${checkedAt}` : ''} · hollow dashed dot = no report in over a day · click a dot for details</span>
            <span style={{ display: 'flex', gap: 10, fontSize: 11, color: TEXT }}>
              {legend.map(([lbl, c]) => <span key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: c, display: 'inline-block' }} />{lbl}</span>)}
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: '50%', border: '2px solid #FFFFFF', boxShadow: `0 0 6px 1px ${T.accent2 || '#FFB020'}`, display: 'inline-block' }} />fault code open (red ring = red code)</span>
            </span>
            {(() => {
              const tq = topFind.trim().toLowerCase();
              const hits = tq.length >= 2 ? pts.filter(p => `${p.unit} ${p.name || ''} ${p.status || ''}`.toLowerCase().includes(tq)).sort((a, b) => String(a.unit).localeCompare(String(b.unit))).slice(0, 12) : [];
              const go = (p) => { setFocus({ unit: p.unit, n: Date.now() }); setTopFind(''); };
              return (
                <span style={{ position: 'relative', marginLeft: 'auto' }}>
                  <input value={topFind} onChange={e => setTopFind(e.target.value)} autoFocus
                    onKeyDown={e => { if (e.key === 'Enter' && hits[0]) go(hits[0]); if (e.key === 'Escape' && topFind) { e.stopPropagation(); setTopFind(''); } }}
                    placeholder="🔍 Find rig — 23-2033, 40AT, R3…"
                    style={{ width: 220, boxSizing: 'border-box', background: '#15171A', border: `1px solid ${ACCENT}88`, borderRadius: 7, padding: '6px 9px', fontSize: 12.5, color: TEXT, outline: 'none' }} />
                  {tq.length >= 2 ? (
                    <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', width: 300, maxHeight: 340, overflowY: 'auto', zIndex: 10001, background: '#1B1D21', border: `1px solid ${T.panelBorder || '#3A3D42'}`, borderRadius: 8, boxShadow: '0 10px 30px #000A', padding: 4 }}>
                      {hits.length ? hits.map(p => (
                        <button key={p.unit} type="button" onClick={() => go(p)} style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', cursor: 'pointer', background: 'transparent', border: 0, borderBottom: `1px solid ${T.panelBorder || '#3A3D42'}55`, color: TEXT, padding: '6px 4px', fontSize: 12 }}>
                          <span style={{ flex: '0 0 9px', height: 9, borderRadius: '50%', background: p.color }} />
                          <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>{p.unit}</b> <span style={{ color: STEEL }}>{p.name} · {p.status}</span></span>
                        </button>
                      )) : <div style={{ fontSize: 12, color: STEEL, padding: 6 }}>No rig matches “{topFind.trim()}”.</div>}
                    </div>
                  ) : null}
                </span>
              );
            })()}
            <button type="button" onClick={() => setFull(false)} style={{ ...btn, padding: '5px 12px', fontSize: 12 }}>✕ Close (Esc)</button>
          </div>
          <div style={{ flex: '1 1 auto', padding: 10, minHeight: 0 }}><MapView points={pts} full height="100%" focus={focus} /></div>
        </div>, document.body) : null}
    </div>
  );
}
